import { Box, Flex, IconButton, Stack, Text, useDisclosure } from '@chakra-ui/react'
import { faBell, faCalendar, faMessage } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useEffect, useState } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'

import { HasuraContextWrapper } from '@/client/context/hasura'
import { useUser } from '@/client/context/user'
import { getDashboardUrl, getMessagesUrl, getScheduleUrl } from '@/client/services/url'
import { hasUserRole } from '@/client/services/user'

import LiveHelpIndicator from '@/components/Community/LiveHelp/LiveHelpIndicator'
import GlobalConversationsPopup from '@/components/Conversation/GlobalConversationsPopup'
import { usePlatformLayoutContext } from '@/components/Layout/context'
import PlatformHeaderBase from '@/components/Layout/PlatformHeaderBase'
import AIGuideButton from '@/components/SATBootcamp/AIGuide/AIGuideButton'
import ShwLink from '@/components/ShwLink'
import ShwLogo from '@/components/UI/ShwLogo'
import useCountUnreadConversations from '@/components/Worlds/hooks/user/useCountUnreadConversations'
import useRealtimeUserNotificationDetails from '@/components/Worlds/hooks/user/useRealtimeUserNotificationDetails'
import useMemberWorlds from '@/components/Worlds/hooks/world/useMemberWorlds'
import SettingsButton from '@/components/Worlds/Settings/SettingsButton'
import NotificationBubble from '@/components/Worlds/UI/NotificationBubble'
import type { BreadcrumbDetails } from '@/components/Worlds/UI/types'

function ChatsNavBubble(): JSX.Element | null {
  const { unreadConversationCount } = useCountUnreadConversations({})
  return unreadConversationCount != null && unreadConversationCount > 0 ? (
    <NotificationBubble value={unreadConversationCount} />
  ) : null
}

function NotificationNavBubble(): JSX.Element | null {
  const { data } = useRealtimeUserNotificationDetails({})
  const unreadNotificationCount = data?.unreadNotificationCount ?? 0

  return unreadNotificationCount > 0 ? <NotificationBubble value={unreadNotificationCount} /> : null
}

type PlatformHeaderProps = {
  heading?: {
    title: string
    breadcrumbs?: BreadcrumbDetails[]
  }
  hideAIGuideButton?: boolean
}

export default function PlatformHeader({
  heading,
  hideAIGuideButton = false,
}: PlatformHeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false)
  const user = useUser()

  const { title, breadcrumbs } = heading ?? {}
  const tutorWorldIds = useMemberWorlds(['id'], 'tutor').map(world => world.id)

  const showAIGuideButton = !hideAIGuideButton && hasUserRole(user, 'user:feature_flag:ai-guide')

  const {
    onToggle: onGlobalConversationsToggle,
    onClose: onGlobalConversationsClose,
    isOpen: globalConversationsOpen,
  } = useDisclosure()

  const { showNotifications, toggleShowNotifications } = usePlatformLayoutContext()

  useEffect(() => {
    const main = document.getElementById('main')

    const handleScroll = () => {
      if (main?.scrollTop != null && main.scrollTop > 0) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }

    main?.addEventListener('scroll', handleScroll)

    return () => {
      main?.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <PlatformHeaderBase
      bgColor={isScrolled ? 'bg' : 'transparent'}
      borderBottom={`1px solid ${isScrolled ? chakraTokenToVar('border.muted') : 'transparent'}`}
      transition="all 0.2s ease-in-out"
      leftContent={
        <Flex direction="row" alignItems="center" gap="8px">
          <ShwLink href={getDashboardUrl()}>
            <IconButton
              variant="icon"
              width="48px"
              height="48px"
              _hover={{ bg: 'bg.subtle' }}
              borderRadius="8px"
              icon={<ShwLogo height="24px" />}
              aria-label="Home"
            />
          </ShwLink>
          {heading == null ? null : (
            <Flex
              direction="row"
              alignItems="center"
              gap="8px"
              display={{ base: 'none', sm: 'flex' }}
            >
              {breadcrumbs?.length === 0 ? null : (
                <Stack direction="row" align="center" spacing="8px" mr="8px">
                  {breadcrumbs?.map(({ label, url: breadcrumbUrl }) => (
                    <React.Fragment key={label}>
                      {breadcrumbUrl == null ? (
                        <Text color="fg.muted">{label}</Text>
                      ) : (
                        <ShwLink href={breadcrumbUrl} variant="subtle-gray">
                          {label}
                        </ShwLink>
                      )}
                      <Text color="fg.muted">/</Text>
                    </React.Fragment>
                  ))}
                </Stack>
              )}
              <Text fontWeight="bold" as="span">
                {title}
              </Text>
            </Flex>
          )}
        </Flex>
      }
      rightContent={
        <>
          <Flex direction="row" justifyContent="flex-end" alignItems="center" gap="8px">
            <Flex direction="row" alignItems="center" gap="8px">
              {showAIGuideButton ? <AIGuideButton onToggle={onGlobalConversationsToggle} /> : null}
              <HasuraContextWrapper LoadingComponent={() => null}>
                <LiveHelpIndicator tutorWorldIds={tutorWorldIds} />
              </HasuraContextWrapper>
              <ShwLink href={getMessagesUrl()}>
                <IconButton
                  variant="icon"
                  width="36px"
                  height="36px"
                  _hover={{ bg: 'bg.subtle' }}
                  borderRadius="8px"
                  icon={
                    <>
                      <>
                        <Box display={{ base: 'none', sm: 'block' }}>
                          <FontAwesomeIcon
                            icon={faMessage}
                            color={chakraTokenToVar('fg')}
                            size="lg"
                          />
                        </Box>
                        <Box display={{ base: 'block', sm: 'none' }}>
                          <FontAwesomeIcon
                            icon={faMessage}
                            color={chakraTokenToVar('fg')}
                            size="lg"
                          />
                        </Box>
                      </>
                      <HasuraContextWrapper LoadingComponent={() => null}>
                        <ChatsNavBubble />
                      </HasuraContextWrapper>
                    </>
                  }
                  aria-label="Home"
                />
              </ShwLink>
              <IconButton
                variant="icon"
                width="36px"
                height="36px"
                _hover={{ bg: 'bg.subtle' }}
                borderRadius="8px"
                icon={
                  <>
                    <>
                      <Box display={{ base: 'none', sm: 'block' }}>
                        <FontAwesomeIcon
                          icon={faBell}
                          color={
                            showNotifications
                              ? chakraTokenToVar('primary.solid')
                              : chakraTokenToVar('fg')
                          }
                          size="lg"
                        />
                      </Box>
                      <Box display={{ base: 'block', sm: 'none' }}>
                        <FontAwesomeIcon
                          icon={faBell}
                          color={
                            showNotifications
                              ? chakraTokenToVar('primary.solid')
                              : chakraTokenToVar('fg')
                          }
                          size="lg"
                        />
                      </Box>
                    </>
                    <HasuraContextWrapper LoadingComponent={() => null}>
                      <NotificationNavBubble />
                    </HasuraContextWrapper>
                  </>
                }
                aria-label="Home"
                onClick={toggleShowNotifications}
                cursor="pointer"
              />
              <ShwLink href={getScheduleUrl({ past: false })}>
                <IconButton
                  variant="icon"
                  width="36px"
                  height="36px"
                  _hover={{ bg: 'bg.subtle' }}
                  borderRadius="8px"
                  icon={
                    <>
                      <Box display={{ base: 'none', sm: 'block' }}>
                        <FontAwesomeIcon
                          icon={faCalendar}
                          color={chakraTokenToVar('fg')}
                          size="lg"
                        />
                      </Box>
                      <Box display={{ base: 'block', sm: 'none' }}>
                        <FontAwesomeIcon
                          icon={faCalendar}
                          color={chakraTokenToVar('fg')}
                          size="lg"
                        />
                      </Box>
                    </>
                  }
                  aria-label="Home"
                />
              </ShwLink>
            </Flex>
            <SettingsButton />
          </Flex>
          {showAIGuideButton ? (
            <HasuraContextWrapper LoadingComponent={() => null}>
              <GlobalConversationsPopup
                isOpen={globalConversationsOpen}
                onClose={onGlobalConversationsClose}
              />
            </HasuraContextWrapper>
          ) : null}
        </>
      }
    />
  )
}
